import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchArticles } from "../../api";
import { ArticleCard } from "../../components/ArticleCard";
import { FilterBtn } from "../../components/FilterBtn";
import { useFilterStore } from "../../store";
import { COMPANIES } from "../../constants/companies";

const CATEGORIES = [
  { value: "", label: "全部" },
  { value: "policy_regulation", label: "政策监管" },
  { value: "product_update",    label: "产品动态" },
  { value: "market_trend",      label: "市场趋势" },
  { value: "company_news",      label: "公司资讯" },
  { value: "risk_event",        label: "风险事件" },
  { value: "technology",        label: "技术创新" },
];

const SOURCE_TYPES = [
  { value: "", label: "全部来源" },
  { value: "official_gov", label: "监管机构" },
  { value: "official_pr",  label: "企业官方" },
  { value: "news",         label: "媒体" },
  { value: "user_upload",  label: "内部上传" },
];

const PAGE_SIZE = 20;

export default function HomePage() {
  const { category, sourceType, company, importantOnly, riskTransmission, setFilter } = useFilterStore();
  const sentinelRef  = useRef<HTMLDivElement>(null);
  const queryClient  = useQueryClient();
  const [newCount, setNewCount] = useState(0);

  const filters = {
    category:       category   || undefined,
    source_type:    sourceType || undefined,
    company:        company    || undefined,
    min_importance: importantOnly ? 4 : undefined,
    has_risk_transmission: riskTransmission ? true : undefined,
    sort_by:        importantOnly ? "importance" : undefined,
  };

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["articles", filters],
      queryFn: ({ pageParam = 1 }) =>
        fetchArticles({ ...filters, page: pageParam, page_size: PAGE_SIZE }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.has_more ? allPages.length + 1 : undefined,
      initialPageParam: 1,
    });

  const knownTotal = data?.pages[0]?.total ?? 0;

  const { data: pollData } = useQuery({
    queryKey: ["articles-poll", filters],
    queryFn: () => fetchArticles({ ...filters, page: 1, page_size: 1 }),
    refetchInterval: 60_000,
    staleTime: 0,
    enabled: knownTotal > 0,
  });

  useEffect(() => {
    const polledTotal = pollData?.total ?? 0;
    if (polledTotal > knownTotal) setNewCount(polledTotal - knownTotal);
  }, [pollData, knownTotal]);

  useEffect(() => {
    setNewCount(0);
  }, [filters.category, filters.source_type, filters.company, filters.sort_by, filters.has_risk_transmission]);

  const handleRefresh = () => {
    setNewCount(0);
    queryClient.invalidateQueries({ queryKey: ["articles", filters] });
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allArticles = data?.pages.flatMap(p => p.items) ?? [];
  const total       = data?.pages[0]?.total ?? 0;

  return (
    <div className="space-y-5">
      {/* Filter toolbar */}
      <div className="px-1 py-2">
        {/* Row 1: categories */}
        <div className="flex items-center gap-0.5 overflow-x-auto pb-1.5 border-b border-border">
          {CATEGORIES.map(cat => (
            <FilterBtn key={cat.value} active={category === cat.value} onClick={() => setFilter("category", cat.value)}>
              {cat.label}
            </FilterBtn>
          ))}
        </div>

        {/* Row 2: source / company / importance */}
        <div className="flex items-center gap-0.5 pt-1.5 flex-wrap">
          {SOURCE_TYPES.map(opt => (
            <FilterBtn key={opt.value} active={sourceType === opt.value} onClick={() => setFilter("sourceType", opt.value)}>
              {opt.label}
            </FilterBtn>
          ))}

          <div className="w-px h-3.5 bg-border mx-1 shrink-0" />

          {COMPANIES.map(opt => (
            <FilterBtn key={opt.value} active={company === opt.value} onClick={() => setFilter("company", opt.value)}>
              {opt.label}
            </FilterBtn>
          ))}

          <div className="flex-1" />

          <FilterBtn
            active={!!importantOnly}
            onClick={() => setFilter("importantOnly", importantOnly ? "" : "1")}
          >
            精选
          </FilterBtn>

          <FilterBtn
            active={!!riskTransmission}
            onClick={() => setFilter("riskTransmission", riskTransmission ? "" : "1")}
          >
            ⚠ 风险传导
          </FilterBtn>
        </div>
      </div>

      {/* New articles banner */}
      {newCount > 0 && (
        <button
          onClick={handleRefresh}
          className="w-full flex items-center justify-center gap-2 py-2 bg-accent-50 border border-accent/20 rounded-lg text-[12px] text-accent-dark dark:text-accent dark:bg-accent/5 dark:border-accent/15 hover:bg-accent/10 transition-colors"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          有 {newCount} 条新资讯，点击刷新
        </button>
      )}

      {/* Count */}
      {total > 0 && (
        <p className="text-[11px] text-ink-faint px-0.5">
          {total} 条资讯{importantOnly && <span className="ml-1.5 text-warning font-medium">· 精选</span>}{riskTransmission && <span className="ml-1.5 text-warning font-medium">· ⚠ 风险传导</span>}
        </p>
      )}

      {/* Article feed */}
      <div className="overflow-hidden">
        {isLoading && (
          <div className="divide-y divide-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-4 py-3 animate-pulse space-y-2">
                <div className="h-2.5 w-20 bg-surface-sunken rounded" />
                <div className="h-3 w-4/5 bg-surface-sunken rounded" />
                <div className="h-2.5 w-3/5 bg-surface-sunken rounded" />
              </div>
            ))}
          </div>
        )}

        {isError && (
          <div className="text-center py-16 text-[13px] text-ink-tertiary">加载失败，请稍后重试</div>
        )}

        {!isLoading && allArticles.length === 0 && !isError && (
          <div className="text-center py-16 text-[13px] text-ink-tertiary">暂无相关资讯</div>
        )}

        {allArticles.length > 0 && (
          <div className="divide-y divide-border">
            {allArticles.map(article => <ArticleCard key={article.id} article={article} />)}
          </div>
        )}

        <div ref={sentinelRef} className="py-4 flex justify-center">
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-[11px] text-ink-faint">
              <div className="w-3 h-3 border-2 border-border border-t-primary-400 rounded-full animate-spin" />
              加载中
            </div>
          )}
          {!hasNextPage && allArticles.length > 0 && !isFetchingNextPage && (
            <span className="text-[11px] text-ink-faint">已加载全部 {allArticles.length} 条</span>
          )}
        </div>
      </div>
    </div>
  );
}
